import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { Router } from "@angular/router";
import {
  CANCELLED_CONTRACT_STATUS,
  GeneralConfirmationModalData,
  GeneralConfirmationModalResult,
  PaymentFrequency,
  PetInsuranceData
} from "../../../models";
import { DataStorageService } from "../../../services/data-storage.service";
import { MatDialog } from "@angular/material/dialog";
import { DialogService } from "../../../services/dialog.service";
import { delay, Subject, takeUntil } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import {
  GeneralConfirmationModalComponent
} from "../../../components/general-confirmation-modal/general-confirmation-modal.component";

@Component({
  selector: 'cpf-insurance-details',
  templateUrl: './insurance-details.component.html',
  styleUrls: ['./insurance-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class InsuranceDetailsComponent implements OnInit, OnDestroy {
  public petData?: PetInsuranceData;
  private destroy$ = new Subject<void>();
  @Input() showBackButton = true;
  @Output() editInsuranceInfoClicked = new EventEmitter<void>();

  /** CI shows the contract as cancelled: it offers no second cancellation. */
  get isCancelled(): boolean {
    return this.petData?.status === CANCELLED_CONTRACT_STATUS;
  }

  public coverages: string[] = [
    "overall-sum-insured",
    "accident-and-illness-coverage",
    "illness-advanced-treatment-coverage",
    "illness-complementary-treatment-coverage",
    "illness-behavioural-treatment-coverage",
    "accident-dental-treatment-coverage",
    "illness-dental-treatment-coverage",
    "emergency-minding-coverage",
    "emergency-treatment-coverage",
    "medical-food-coverage",
    "preventive-care-coverage",
    "preexisting-conditions-coverage",
    "worldwide-protection-coverage"
  ]

  constructor(private router: Router,
              private dataStorageService: DataStorageService,
              private dialog: MatDialog,
              private dialogService: DialogService,
              private translate: TranslateService) {
  }

  ngOnInit() {
    // Delay is needed to prevent scroll issue
    this.dataStorageService.selectedData$.pipe(
      takeUntil(this.destroy$),
      delay(200)
    ).subscribe((data) => {
      if (data) {
        this.petData = data;
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getSubscriptionInterval() {
    const values = Object.values(PaymentFrequency);
    const index = values.indexOf(this.petData!.payment_interval) || 0;
    const interval = Object.keys(PaymentFrequency)[index];
    const intervalLabel = this.translate.instant(`common.subscription-interval.${interval}`);
    return intervalLabel;
  }

  getSubscriptionCost() {
    return this.petData ? this.petData.yearly_gross_premium : 0;
  }

  navigateBack() {
    this.router.navigate(['calingo', 'pet', 'insurance']);
  }

  openInfo(titleKey: string, messageKey: string) {
    this.translate.get([titleKey, messageKey]).subscribe((values) => {
      const title = values[titleKey];
      const message = values[messageKey];

      const dialogRef = this.dialogService.openDialog(GeneralConfirmationModalComponent, {
        data: {
          title: title,
          message: message,
          confirmButton: GeneralConfirmationModalResult.Confirm,
          confirmButtonText: 'OK',
          useInnerHtml: true,
          textAlign: 'left'
        } as GeneralConfirmationModalData
      });

      dialogRef.afterClosed().subscribe(() => {

      });
    })
  }

  editInsuranceInfo() {
    if (this.editInsuranceInfoClicked.observed) {
      this.editInsuranceInfoClicked.emit();
      return;
    }
    this.router.navigate(['calingo', 'pet', 'insurance', 'edit-insurance-info'])
  }

  cancelInsurance() {
    this.translate.get('insurance-details.cancel-insurance-title').subscribe((title) => {
      const dialogRef = this.dialogService.openDialog(GeneralConfirmationModalComponent, {
        data: {
          title: title,
          confirmButton: GeneralConfirmationModalResult.Confirm,
          cancelButton: GeneralConfirmationModalResult.Cancel,
          isImage: true,
          imageSrc: '/assets/cancel-contact-image.png'
        } as GeneralConfirmationModalData
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result === GeneralConfirmationModalResult.Confirm) {
          this.router.navigate(['calingo', 'pet', 'insurance', 'cancel']);
          return;
        }

        console.log('cancel');

        return;
      });
    });
  }

  showCurrentDeductible() {
    if (!this.petData) {
      return
    }

    const petFullYears = (new Date().getTime() - new Date(this.petData.birthdate).getTime()) / (1000 * 60 * 60 * 24 * 365);
    const deductibles = petFullYears < 9 ? this.petData.deductible_percentage_until_9_years : this.petData.deductible_percentage_after_9_years;
    return (100 - deductibles).toFixed(0);
  }

  getRenewDate() {
    if (!this.petData) {
      return ""
    }

    let endDate = new Date(this.petData.end_date);
    endDate.setDate(endDate.getDate() + 1);

    return endDate;
  }
}
